import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  AlertCircle,
  CheckCircle2,
  FileText,
  Loader2,
  Menu,
  X,
  Plus,
  MessageSquare,
  Trash2,
} from 'lucide-react';
import MessageList from './MessageList';
import { getAccessibleApps } from '../../config/appRegistry';
import MessageInput, { MessageInputHandle } from './MessageInput';
import DocumentUpload from '../Document/DocumentUpload';
import ConfirmationModal from '../UI/Modal';
import { Message, Document } from '../../types';
import { authService } from '../../services/authService';
import { APP_GROUPS } from '../../config/appGroups';
import './ChatInterface.css';

// Mobile hook
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);
    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);

  return isMobile;
};

interface ChatSession {
  id: string;
  title: string;
  messages: any[];
  timestamp: Date;
  messageCount: number;
}

interface ChatInterfaceProps {
  messages: Message[];
  isLoading: boolean;
  /** Fired when a user clicks through an app handoff card. */
  onAppHandoff?: (appKey: string) => void;
  onSendMessage: (message: string) => void;
  onFileUpload?: (file: File) => Promise<void>;
  onStopGeneration?: () => void;
  inputRef?: React.Ref<MessageInputHandle>;
  documents?: Document[];
  onDeleteDocument?: (documentId: string) => void;
  // Mobile sidebar props
  chatSessions?: ChatSession[];
  onNewChat?: () => void;
  onSelectChat?: (chatId: string) => void;
  onDeleteChat?: (chatId: string) => void;
  currentChatId?: string | null;
}

const ChatInterface: React.FC<ChatInterfaceProps> = ({
  messages,
  isLoading,
  onSendMessage,
  onFileUpload,
  onStopGeneration,
  inputRef,
  documents = [],
  onDeleteDocument,
  chatSessions = [],
  onNewChat,
  onSelectChat,
  onDeleteChat,
  currentChatId,
  onAppHandoff,
}) => {
  // Empty-state chips. The first two are things this assistant answers
  // itself; the rest advertise the internal apps THIS user can open, so the
  // first screen doubles as a personalized tour of the suite. Apps the user
  // lacks the AD group for are never listed -- getAccessibleApps() fails
  // closed, including when the groups claim is in overage.
  const welcomeSuggestions = useMemo(() => {
    const base = [
      { prompt: 'What meetings do I have today?', hint: 'Your calendar' },
      { prompt: 'Summarize my recent emails', hint: 'Your inbox' },
    ];
    const appChips = getAccessibleApps().slice(0, 4)
      .map((app) => ({ prompt: app.starterPrompt, hint: `${app.label} — ${app.blurb}` }));
    return [...base, ...appChips];
  }, []);

  const isMobile = useIsMobile();
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState<{ id: string; name: string } | null>(null);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [chatToDelete, setChatToDelete] = useState<{ id: string; title: string } | null>(null);
  
  // Get user info from auth service
  const userInfo = authService.getUserInfo();
  
  const handleSignOut = async () => {
    try {
      await authService.logout();
    } catch (error) {
      console.error('Sign out error:', error);
    }
  };
  const formatDateTime = (date: Date) => {
    const dateStr = date.toLocaleDateString([], { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
    const timeStr = date.toLocaleTimeString([], { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
    
    return `${dateStr} • ${timeStr}`;
  };

  const getDocumentStatus = (document: Document) => document.status || 'ready';

  const getDocumentStatusLabel = (document: Document) => {
    const status = getDocumentStatus(document);
    if (status === 'processing') return 'Processing';
    if (status === 'failed') return 'Failed';
    return 'Ready';
  };

  const getDocumentStatusIcon = (document: Document) => {
    const status = getDocumentStatus(document);
    if (status === 'processing') return <Loader2 size={14} className="context-status-spinner" />;
    if (status === 'failed') return <AlertCircle size={14} />;
    return <CheckCircle2 size={14} />;
  };

  const getDocumentMeta = (document: Document) => {
    const parts: string[] = [];
    if (document.summary?.rowCount) {
      parts.push(`${document.summary.rowCount.toLocaleString()} rows`);
    }
    if (document.summary?.sheetCount) {
      parts.push(`${document.summary.sheetCount.toLocaleString()} sheets`);
    }
    if (!parts.length && document.fileSize) {
      const kb = Math.max(1, Math.round(document.fileSize / 1024));
      parts.push(`${kb.toLocaleString()} KB`);
    }
    return parts.join(' | ');
  };

  return (
    <div className="chat-interface">
      <motion.header 
        className="chat-header"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
      >
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: '6px',
          flexWrap: 'wrap',
          flex: 1,
          overflow: 'hidden'
        }}>
          {isMobile && (
            <button
              className="mobile-menu-button"
              onClick={() => setIsMobileSidebarOpen(true)}
              aria-label="Open menu"
            >
              <Menu size={24} />
            </button>
          )}
          
          {/* Training - visible to all RR employees */}
          {APP_GROUPS.TRAINING && authService.hasGroupAccess(APP_GROUPS.TRAINING) && (
            <button 
              className="training-button" 
              title="Training Site" 
              onClick={() => window.open('https://training.rrrealty.com/', '_blank')}
              style={{
                padding: '6px 12px',
                backgroundColor: 'var(--color-primary)',
                color: 'white',
                border: 'none',
                borderRadius: 'var(--radius-md)',
                cursor: 'pointer',
                fontSize: 'var(--font-size-sm)',
                fontWeight: 'var(--font-weight-medium)',
                transition: 'var(--transition-fast)'
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--color-primary-dark)'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'var(--color-primary)'}
            >
              Training
            </button>
          )}
          
          {/* bumbl.b - only visible if user has access */}
          {APP_GROUPS.BUMBLB && authService.hasGroupAccess(APP_GROUPS.BUMBLB) && (
            <button 
              className="bumblb-button" 
              title="bumbl.b - Invoice Application" 
              onClick={() => window.open('https://invoice.rrrealty.com/', '_blank')}
              style={{
                padding: '6px 12px',
                backgroundColor: '#FFF9C4',
                color: '#7A6F00',
                border: 'none',
                borderRadius: 'var(--radius-md)',
                cursor: 'pointer',
                fontSize: 'var(--font-size-sm)',
                fontWeight: 'var(--font-weight-medium)',
                transition: 'var(--transition-fast)'
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#FFF59D'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#FFF9C4'}
            >
              bumbl.b
            </button>
          )}
          
          {/* Space Available - only visible if user has access */}
          {APP_GROUPS.SPACE_AVAILABLE && authService.hasGroupAccess(APP_GROUPS.SPACE_AVAILABLE) && (
            <button 
              className="space-available-button" 
              title="Space Available" 
              onClick={() => window.open('https://space.rrrealty.com/', '_blank')}
              style={{
                padding: '6px 12px',
                backgroundColor: '#BBDEFB',
                color: '#0D47A1',
                border: 'none',
                borderRadius: 'var(--radius-md)',
                cursor: 'pointer',
                fontSize: 'var(--font-size-sm)',
                fontWeight: 'var(--font-weight-medium)',
                transition: 'var(--transition-fast)'
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#90CAF9'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#BBDEFB'}
            >
              Space Available
            </button>
          )}
          
          {/* ResChat - only visible if user has access */}
          {APP_GROUPS.RESCHAT && authService.hasGroupAccess(APP_GROUPS.RESCHAT) && (
            <button 
              className="reschat-button" 
              title="ResChat - Residential Chat" 
              onClick={() => window.open('https://reschat.rrrealty.com/', '_blank')}
              style={{
                padding: '6px 12px',
                backgroundColor: '#E1BEE7',
                color: '#4A148C',
                border: 'none',
                borderRadius: 'var(--radius-md)',
                cursor: 'pointer',
                fontSize: 'var(--font-size-sm)',
                fontWeight: 'var(--font-weight-medium)',
                transition: 'var(--transition-fast)'
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#CE93D8'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#E1BEE7'}
            >
              ResChat
            </button>
          )}
          
          {/* InsightManager - only visible if user has access */}
          {APP_GROUPS.INSIGHT_MANAGER && authService.hasGroupAccess(APP_GROUPS.INSIGHT_MANAGER) && (
            <button 
              className="insight-manager-button" 
              title="InsightManager - Insight Manager Application" 
              onClick={() => window.open('https://insightmanager.rrrealty.com/', '_blank')}
              style={{
                padding: '6px 12px',
                backgroundColor: '#FFE0B2',
                color: '#E65100',
                border: 'none',
                borderRadius: 'var(--radius-md)',
                cursor: 'pointer',
                fontSize: 'var(--font-size-sm)',
                fontWeight: 'var(--font-weight-medium)',
                transition: 'var(--transition-fast)'
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#FFCC80'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#FFE0B2'}
            >
              InsightManager
            </button>
          )}
          
          {/* LeaseChat - only visible if user has access */}
          {APP_GROUPS.LEASE_CHAT && authService.hasGroupAccess(APP_GROUPS.LEASE_CHAT) && (
            <button 
              className="lease-chat-button" 
              title="LeaseChat - Lease Application" 
              onClick={() => window.open('https://lease.rrrealty.com/', '_blank')}
              style={{
                padding: '6px 12px',
                backgroundColor: '#F8BBD0',
                color: '#880E4F',
                border: 'none',
                borderRadius: 'var(--radius-md)',
                cursor: 'pointer',
                fontSize: 'var(--font-size-sm)',
                fontWeight: 'var(--font-weight-medium)',
                transition: 'var(--transition-fast)'
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#F48FB1'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#F8BBD0'}
            >
              LeaseChat
            </button>
          )}
        </div>
        <div className="user-info">
          <span className="user-name">{userInfo?.email || 'User'}</span>
          <button className="signout-button" title="Sign Out" onClick={handleSignOut}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
              <polyline points="16,17 21,12 16,7"/>
              <line x1="21" y1="12" x2="9" y2="12"/>
            </svg>
          </button>
        </div>
      </motion.header>

      <div className="chat-content">
        {documents.length > 0 && (
          <div className="chat-context-panel" aria-label="Active document context">
            <div className="chat-context-header">
              <span className="chat-context-title">Active context</span>
            </div>
            <div className="chat-context-documents">
              {documents.map((doc) => (
                <div key={doc.id} className={`context-document context-document-${getDocumentStatus(doc)}`}>
                  <FileText size={15} className="context-document-icon" />
                  <div className="context-document-body">
                    <div className="context-document-name" title={doc.originalFileName}>
                      {doc.originalFileName}
                    </div>
                    <div className="context-document-meta">
                      <span className="context-document-status">
                        {getDocumentStatusIcon(doc)}
                        {getDocumentStatusLabel(doc)}
                      </span>
                      {getDocumentMeta(doc) && (
                        <span className="context-document-detail">{getDocumentMeta(doc)}</span>
                      )}
                    </div>
                  </div>
                  <button
                    className="context-document-remove"
                    onClick={() => setDocumentToDelete({ id: doc.id, name: doc.originalFileName })}
                    title="Remove document"
                    aria-label={`Remove ${doc.originalFileName}`}
                  >
                    <X size={14} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {messages.length === 0 ? (
          <motion.div 
            className="welcome-screen"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <div className="welcome-content">
              <div className="welcome-icon">
                <img 
                  src="/images/RAIlogo.png" 
                  alt="R&R Realty AI Logo" 
                  className="rr-logo-image"
                  width="80"
                  height="80"
                />
              </div>
              <h2 className="welcome-title">Welcome to R&R Realty AI</h2>
              <p className="welcome-description">
                I'm your intelligent assistant, ready to help with any questions or tasks.
                Start a conversation by typing a message below.
              </p>
              <div className="welcome-suggestions">
                {welcomeSuggestions.map((suggestion) => (
                  <motion.button
                    key={suggestion.prompt}
                    className="suggestion-chip"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => onSendMessage(suggestion.prompt)}
                    title={suggestion.hint}
                  >
                    {suggestion.prompt}
                  </motion.button>
                ))}
              </div>
            </div>
          </motion.div>
        ) : (
          <MessageList messages={messages} isLoading={isLoading} onAppHandoff={onAppHandoff} />
        )}
      </div>

      <MessageInput
        ref={inputRef}
        onSendMessage={onSendMessage}
        onFileUpload={onFileUpload}
        onStopGeneration={onStopGeneration}
        disabled={isLoading}
      />
      
      <DocumentUpload
        isOpen={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
        onUpload={async (file) => {
          if (onFileUpload) {
            await onFileUpload(file);
          }
          setIsUploadModalOpen(false);
        }}
      />
      
      <ConfirmationModal
        isOpen={documentToDelete !== null}
        title="Delete Document"
        message={
          <>
            Are you sure you want to delete <span className="document-name">{documentToDelete?.name}</span>? This action cannot be undone.
          </>
        }
        confirmText="Delete"
        cancelText="Cancel"
        isDestructive={true}
        onConfirm={() => {
          if (documentToDelete && onDeleteDocument) {
            onDeleteDocument(documentToDelete.id);
          }
          setDocumentToDelete(null);
        }}
        onCancel={() => setDocumentToDelete(null)}
      />

      {/* Mobile Sidebar Overlay */}
      <AnimatePresence>
        {isMobile && isMobileSidebarOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              className="mobile-sidebar-backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsMobileSidebarOpen(false)}
            />
            
            {/* Mobile Sidebar */}
            <motion.div
              className="mobile-sidebar"
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
              transition={{ type: 'tween', duration: 0.3 }}
            >
              <div className="mobile-sidebar-header">
                <h2 className="mobile-sidebar-title">R&R Realty AI</h2>
                <button
                  className="mobile-sidebar-close"
                  onClick={() => setIsMobileSidebarOpen(false)}
                  aria-label="Close menu"
                >
                  <X size={24} />
                </button>
              </div>

              <div className="mobile-sidebar-content">
                {/* New Chat Section */}
                <section className="mobile-sidebar-section">
                  <motion.button
                    className="mobile-new-chat-button"
                    onClick={() => {
                      onNewChat?.();
                      setIsMobileSidebarOpen(false);
                    }}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <Plus size={18} />
                    New Chat
                  </motion.button>
                </section>

                {/* Chat History Section */}
                <section className="mobile-sidebar-section">
                  <h3 className="mobile-section-title">Recent Chats</h3>
                  <div className="mobile-chat-history">
                    {chatSessions.length === 0 ? (
                      <div className="mobile-empty-state">
                        <MessageSquare size={32} />
                        <p>No recent chats</p>
                      </div>
                    ) : (
                      <div className="mobile-chat-items">
                        {chatSessions.map((session) => (
                          <motion.div
                            key={session.id}
                            className={`mobile-chat-item ${currentChatId === session.id ? 'active' : ''}`}
                            whileHover={{ scale: 1.02 }}
                            onClick={() => {
                              onSelectChat?.(session.id);
                              setIsMobileSidebarOpen(false);
                            }}
                          >
                            <div className="mobile-chat-icon">
                              <MessageSquare size={16} />
                            </div>
                            <div className="mobile-chat-info">
                              <p className="mobile-chat-title" title={session.title}>
                                {session.title}
                              </p>
                              <div className="mobile-chat-meta">
                                <span>{session.messageCount} messages • {formatDateTime(session.timestamp)}</span>
                              </div>
                            </div>
                            <button
                              className="mobile-chat-delete"
                              aria-label={`Delete chat: ${session.title}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                setChatToDelete({ id: session.id, title: session.title });
                              }}
                            >
                              <Trash2 size={14} />
                            </button>
                          </motion.div>
                        ))}
                      </div>
                    )}
                  </div>
                </section>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Mobile Chat Delete Confirmation */}
      <ConfirmationModal
        isOpen={chatToDelete !== null}
        title="Delete Conversation"
        message={`Are you sure you want to delete "${chatToDelete?.title}"? This will permanently delete all messages and documents in this conversation. This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        isDestructive={true}
        onConfirm={() => {
          if (chatToDelete) {
            onDeleteChat?.(chatToDelete.id);
          }
          setChatToDelete(null);
        }}
        onCancel={() => setChatToDelete(null)}
      />
    </div>
  );
};

export default ChatInterface;
